// App shell chrome — Blueprint §2: three-tier sidebar (CARE / OPERATE
// [advanced] / MANAGE), user card, mode chip (R-030), version footer; top bar
// with view title, disabled search placeholder and action slots (programme
// pill, Live chip, Help — filled by their own modules); mobile 5-tab bottom
// bar. Ported structures: legacy sidebar (v20 lines 486-509), top bar
// (512-528), mob-tabs (872-878).
//
// Everything renders FROM THE STORE: the shell subscribes to the UI and
// COUNTS slices and re-renders; nothing else may poke shell DOM directly.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { trapStep, FOCUSABLE } from '../ui/modal.js';
import { dismissCoach } from '../ui/coachmark.js';
import { netErr, NET_READ_MSG } from '../errors.js';
import { nextMode, writeMode, modeChipLabel, modeChipTitle, modeToast } from '../mode.js';
import {
  ROUTES,
  TIERS,
  DEFAULT_VIEW,
  visibleRoutes,
  canAccess,
  navigate,
  reloadView,
} from './registry.js';

const TIER_LABELS = { [TIERS.CARE]: 'Care', [TIERS.OPERATE]: 'Operate', [TIERS.MANAGE]: 'Manage' };

// Sidebar count badges per route id (legacy cnt-t/i/p/s: today due+overdue,
// pending consents, total cases, serious AEs). Phase 2 loaders fill the
// COUNTS slice with real numbers; the shell just renders whatever is there.
export const COUNT_SEED = Object.freeze({ today: 0, consents: 0, patients: 0, safety: 0 });

/**
 * Pure: badge text for a route's count — 0/absent/invalid hides the badge
 * (legacy hid zero counts rather than shouting "0" on every row).
 * @param {any} counts COUNTS slice value
 * @param {string} routeId
 * @returns {string|null} text to show, or null to hide the badge
 */
export function countBadge(counts, routeId) {
  const n = counts ? counts[routeId] : 0;
  return typeof n === 'number' && n > 0 ? String(n) : null;
}

/**
 * Avatar initials — exact port of legacy init() (line ~1012): one word gives
 * its first two letters, several words give first + last initials.
 * @param {string|null|undefined} n full name
 * @returns {string}
 */
export function initials(n) {
  if (!n) return '?';
  // A trailing parenthesised annotation — "(demo)", "(locum)" — is not a
  // surname; drop it so "Jane Wanjiru (demo)" reads JW, never "J(".
  const p = String(n).trim().replace(/\s*\([^)]*\)\s*$/, '').split(/\s+/);
  if (!p[0]) return '?';
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

// ─── Mobile bottom bar (legacy mob-tabs, 872-878) ──────────────────────────
// 5 tabs: Today / Consents / Board / Patients / Search. Search is a disabled
// placeholder until the Cmd-K palette arrives in Phase 2.

const MOB_TABS = [
  {
    id: 'today',
    label: 'Today',
    icon: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  },
  {
    id: 'consents',
    label: 'Consents',
    icon: '<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>',
  },
  {
    id: 'board',
    label: 'Board',
    icon: '<rect x="3" y="3" width="6" height="18" rx="1"/><rect x="11" y="3" width="6" height="12" rx="1"/>',
  },
  {
    id: 'patients',
    label: 'Patients',
    icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>',
  },
  // Training holds the 5th slot (where a disabled Search placeholder used to
  // sit). Nurses are phone-first, and a course they cannot find is a course
  // they do not take — a dead placeholder does not outrank it for thumb space.
  {
    id: 'training',
    label: 'Training',
    icon: '<path d="M22 10 12 5 2 10l10 5 10-5z"/><path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5"/>',
  },
];

// Phase 2 shipped long ago and this control did not. Copy that names a phase
// which has already passed is worse than copy that admits the gap: it reads as
// a promise to anyone who does not know the release history. The Cmd-K palette
// is a RECORDED DEFERRAL (docs/PARITY_GATE.md §7) — until it lands, the
// placeholder points at the navigation that does exist.
const SEARCH_DISABLED_TITLE = 'Patient search is not built yet — use Patients or the Board';

/** @param {string} paths @returns {string} */
function svg(paths) {
  return (
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}

// ─── Mobile nav drawer (F-NAV-05, legacy toggleSidebar) ────────────────────
//
// The bottom bar carries five tabs; the app has eleven routes. Without this,
// Safety, Reports, Billing, Import and Admin were UNREACHABLE on any phone —
// the sidebar was simply `display:none` under 768px. The drawer is the same
// <aside> the desktop uses (one nav, one set of gating rules, no second copy to
// drift), slid off-canvas and brought back over a scrim.
//
// P2 house rule: the scrim must hit-test on top. It is a real element above the
// bottom bar (z 40) and below toasts/modals, not a pointer-events:none tint.

/**
 * Pure: should the drawer be open after this event? Kept separate from the DOM
 * so the close-on-navigate rule is testable.
 * @param {boolean} open current state
 * @param {'toggle'|'open'|'close'} intent
 * @returns {boolean}
 */
export function drawerNext(open, intent) {
  if (intent === 'open') return true;
  if (intent === 'close') return false;
  return !open;
}

/**
 * Pure: everything the DOM must reflect for a drawer state, in one place so the
 * pieces cannot drift — the burger's aria-expanded, the scrim, the body scroll
 * lock, and `mainInert`: while the drawer covers the screen the view behind it
 * must leave the tab order AND the accessibility tree (review A-04 — the scrim
 * stopped taps, but a VoiceOver/TalkBack user swiped straight from the menu
 * into the covered view).
 * @param {boolean} open
 * @returns {{open: boolean, expanded: 'true'|'false', scrimHidden: boolean, lock: boolean, mainInert: boolean}}
 */
export function drawerPlan(open) {
  const o = !!open;
  return { open: o, expanded: o ? 'true' : 'false', scrimHidden: !o, lock: o, mainInert: o };
}

let _drawerOpen = false;
/** @type {((ev: any) => void)|null} */
let _drawerTrap = null;

/**
 * The phone breakpoint as a media query. MUST stay in step with the
 * `@media (max-width: 768px)` block in app.css: above it the drawer, the
 * scrim AND the burger are all display:none, so an open drawer leaves no
 * on-screen control able to close it.
 */
export const PHONE_MQ = '(max-width: 768px)';

/**
 * Pure: must an open drawer be forced shut for this viewport?
 *
 * Opening the drawer marks `.main` inert + aria-hidden (drawerPlan) and only
 * closing it clears that. A nurse who taps the burger and then rotates to
 * landscape (393 → 844px) crosses the breakpoint: the drawer, the scrim and
 * the burger all vanish, the layout looks entirely normal, and the top bar,
 * the whole view and the bottom tabs are dead to touch AND to keyboard. The
 * 769–1024px tablet / split-screen band has no natural "rotate back" either.
 * @param {boolean} isPhone does PHONE_MQ currently match?
 * @param {boolean} open is the drawer open?
 * @returns {boolean}
 */
export function drawerShouldClose(isPhone, open) {
  return !!open && !isPhone;
}

/**
 * Apply the drawer state to the DOM. `aria-expanded` on the button and
 * `inert`-like focus handling matter here: an off-canvas sidebar whose buttons
 * are still tabbable is a keyboard trap that leads nowhere visible.
 * @param {boolean} open
 * @returns {void}
 */
export function setDrawer(open) {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const plan = drawerPlan(open);
  _drawerOpen = plan.open;
  const shell = doc.querySelector('.shell');
  const btn = doc.getElementById('nav-burger');
  const scrim = doc.getElementById('nav-scrim');
  if (shell) shell.classList.toggle('drawer-open', _drawerOpen);
  if (btn) btn.setAttribute('aria-expanded', plan.expanded);
  if (scrim) scrim.hidden = plan.scrimHidden;
  // Body scroll lock. A class, never an inline style — the deployed CSP blocks
  // `style=` outright, so a JS-set style here would silently do nothing in
  // production while working perfectly in dev. That asymmetry is exactly the
  // bug this house rule exists to prevent.
  if (doc.body) doc.body.classList.toggle('nav-locked', plan.lock);
  // The covered view leaves the tab order and the a11y tree while the drawer
  // is up (see drawerPlan). `inert` is the attribute that actually does this;
  // aria-hidden rides along for engines that predate it.
  const main = doc.querySelector('.main');
  if (main) {
    if (plan.mainInert) {
      main.setAttribute('inert', '');
      main.setAttribute('aria-hidden', 'true');
    } else {
      main.removeAttribute('inert');
      main.removeAttribute('aria-hidden');
    }
  }
  const side = doc.querySelector('.sidebar');
  // NOTE: the closed drawer is taken out of the tab order and the a11y tree by
  // `visibility: hidden` in the phone media query, NOT here. Doing it in JS
  // would need a matchMedia check, and any path that skipped setDrawer() after
  // a resize would leave the DESKTOP sidebar aria-hidden. CSS gets the media
  // query right for free.
  if (_drawerOpen && side) {
    // A coachmark anchors to a control in the view — which the scrim has just
    // covered. Left up, it floats over the menu pointing at something the user
    // can no longer reach. It is once-per-user and dismissCoach() does not mark
    // it seen, so the tip still gets its turn on the next render.
    dismissCoach();
    const first = side.querySelector('button,a[href]');
    if (first && first.focus) {
      try {
        first.focus();
      } catch {
        /* focus best-effort */
      }
    }
    // Trap Tab inside the drawer while it is open, with the SAME pure step
    // function the modal uses. Without this, Tab walks out of a menu that
    // covers the whole screen and into the view behind the scrim: focus lands
    // on controls the user cannot see and cannot reach with a thumb. Bound on
    // the DOCUMENT (capture), as the modal's trap is: a listener on the aside
    // only fires once focus is already inside it.
    if (!_drawerTrap) {
      _drawerTrap = (/** @type {any} */ ev) => {
        if (ev.key !== 'Tab' || !_drawerOpen) return;
        const panel = doc.querySelector('.sidebar');
        if (!panel) return;
        const list = [...panel.querySelectorAll(FOCUSABLE)].filter(
          (/** @type {any} */ el) => el.offsetParent !== null || el === doc.activeElement
        );
        const idx = list.indexOf(doc.activeElement);
        const step = trapStep(ev.shiftKey, panel.contains(doc.activeElement) ? idx : -1, list.length);
        if (step === null) return;
        ev.preventDefault();
        const target = step === -2 ? panel : list[step];
        if (target && target.focus) target.focus();
      };
    }
    doc.addEventListener('keydown', _drawerTrap, true);
  } else if (_drawerTrap) {
    doc.removeEventListener('keydown', _drawerTrap, true);
  }
}

/** Does the phone breakpoint match right now? No matchMedia → assume phone,
 *  which makes the viewport guard a no-op rather than a surprise.
 *  @param {any} [win] @returns {boolean} */
function phoneViewport(win) {
  const w = win || /** @type {any} */ (globalThis);
  if (!w || typeof w.matchMedia !== 'function') return true;
  try {
    const mql = w.matchMedia(PHONE_MQ);
    return !mql || !!mql.matches;
  } catch {
    return true; // a refusing matchMedia must not freeze the app either
  }
}

/**
 * Force the drawer shut whenever the viewport has left phone width, and clear
 * anything an open drawer left on the DOM (`.main`'s inert + aria-hidden, the
 * body scroll lock, the focus trap). Safe to call at any time.
 *
 * NOT closeDrawer(): that returns focus to the burger, which above the
 * breakpoint is display:none — focusing it would silently drop focus to
 * <body>. Above 768px the sidebar is the ordinary desktop aside, so focus that
 * is already inside it is visible and reachable; leave it where it is.
 * @param {any} [win] injectable for tests
 * @returns {boolean} true when the phone breakpoint still matches
 */
export function syncDrawerToViewport(win) {
  const phone = phoneViewport(win);
  if (!phone) setDrawer(false);
  return phone;
}

/** @type {any} */
let _mql = null;
/** @type {((ev?: any) => void)|null} */
let _mqHandler = null;

/**
 * Watch the phone breakpoint and close the drawer the moment the viewport
 * leaves it. Idempotent; returns a stop function (tests use it, the app never
 * needs to — the listener lives as long as the page).
 *
 * The pure drawerPlan() test cannot reach this: nothing about the resize path
 * is expressible as a state function of the drawer alone. It has its own guard
 * in test/shell.test.js.
 * @param {any} [win] injectable for tests
 * @returns {() => void}
 */
export function watchViewport(win) {
  const noop = () => {};
  const w = win || /** @type {any} */ (globalThis);
  if (_mql) return noop; // already watching
  if (!w || typeof w.matchMedia !== 'function') return noop;
  /** @type {any} */
  let mql = null;
  try {
    mql = w.matchMedia(PHONE_MQ);
  } catch {
    return noop;
  }
  if (!mql) return noop;
  const handler = () => void syncDrawerToViewport(w);
  // addListener is the deprecated spelling; Safari only grew addEventListener
  // on MediaQueryList in 14, and this app is used on older clinic handsets.
  if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handler);
  else if (typeof mql.addListener === 'function') mql.addListener(handler);
  else return noop;
  _mql = mql;
  _mqHandler = handler;
  return () => {
    if (!_mql || !_mqHandler) return;
    if (typeof _mql.removeEventListener === 'function') _mql.removeEventListener('change', _mqHandler);
    else if (typeof _mql.removeListener === 'function') _mql.removeListener(_mqHandler);
    _mql = null;
    _mqHandler = null;
  };
}

/**
 * Put focus on the view region — what the "Skip to main content" control does
 * (A-09, corrected). The control used to be `<a href="#view">`, and the router
 * reads the first path segment of the hash as a route id: 'view' is not one,
 * so every use of the skip link resolved to the default route and navigated
 * the user to Today. Focusing `#view` directly moves the user where they asked
 * to go and writes no hash, so nothing about the current route changes.
 *
 * `#view` is `<main tabindex="-1">` and only exists once the shell has mounted;
 * on the login screen this is a no-op.
 * @returns {boolean} true when focus was actually moved
 */
export function focusView() {
  const doc = /** @type {any} */ (globalThis).document;
  const host = doc && doc.getElementById('view');
  if (!host || typeof host.focus !== 'function') return false;
  try {
    host.focus();
  } catch {
    return false; // focus is best-effort, exactly as elsewhere in this file
  }
  return true;
}

/** Close the drawer if it is open, and return focus to the burger. @returns {void} */
export function closeDrawer() {
  if (!_drawerOpen) return;
  setDrawer(false);
  const doc = /** @type {any} */ (globalThis).document;
  const btn = doc && doc.getElementById('nav-burger');
  if (btn && btn.focus) {
    try {
      btn.focus();
    } catch {
      /* trigger may be gone */
    }
  }
}

// ─── Sync control (F-NAV-03) ───────────────────────────────────────────────
//
// Legacy had a Sync button (v20 refreshData, line 526) and its offline copy
// told users to press it. 2.0 shipped the chip WITHOUT the button, so the only
// remedy the app could honestly name was "reload the page" — which on a phone
// on a bad connection costs the whole bundle again. This restores the control,
// and realtime.js restores the honest copy that points at it.
//
// It re-runs the ACTIVE view's loader through the registry hook — the shell
// never learns what any individual view loads.

const SYNC_ICON =
  '<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>' +
  '<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>';

/** Copy for the case where the active view has nothing to re-fetch. */
export const SYNC_NOOP_MSG = 'Nothing to refresh on this screen';
export const SYNC_FAIL_MSG = 'Could not refresh — the screen still shows the last good data';

let _syncing = false;

/**
 * Pure: the button's presentation for a given busy state. Split out so the
 * label/aria-disabled/aria-busy triple is testable without a DOM.
 *
 * `ariaDisabled`, NOT the `disabled` property: disabling the element the
 * keyboard user has just activated drops focus to <body>, and re-enabling it
 * does not bring focus back — pressing Sync teleported the user to the top of
 * the document (review A-07). The double-fire guard is doSync()'s own
 * `_syncing` latch, which never needed the attribute.
 * @param {boolean} busy
 * @returns {{label: string, ariaDisabled: 'true'|'false', busy: 'true'|'false'}}
 */
export function syncBtnState(busy) {
  return { label: busy ? 'Syncing…' : 'Sync', ariaDisabled: busy ? 'true' : 'false', busy: busy ? 'true' : 'false' };
}

/** @param {boolean} busy @returns {void} */
function paintSync(busy) {
  const doc = /** @type {any} */ (globalThis).document;
  const btn = doc && doc.getElementById('tb-sync');
  if (!btn) return;
  const s = syncBtnState(busy);
  btn.setAttribute('aria-disabled', s.ariaDisabled);
  btn.setAttribute('aria-busy', s.busy);
  btn.classList.toggle('busy', !!busy);
  const lbl = btn.querySelector('.tb-sync-lbl');
  if (lbl) lbl.textContent = s.label;
  btn.setAttribute('aria-label', s.label + ' — reload this view');
}

/** Re-run the active view's loader. @returns {Promise<void>} */
async function doSync() {
  if (_syncing) return; // double-tap on a slow link must not fire two loads
  const view = (store.get(SLICES.UI) || {}).view;
  if (!view) return;
  _syncing = true;
  paintSync(true);
  try {
    const r = await reloadView(view);
    if (!r.ok) {
      // netErr, never a bare message: the user gets an error ref they can read
      // out, and the on-device log gets the row. Failing to refresh is NOT a
      // reason to blank the screen — the view keeps its last good data.
      // `reported` means the view already toasted this exact failure (Reports,
      // Billing); saying it twice does not make it truer.
      if (!(/** @type {any} */ (r).reported)) {
        toast(netErr(r.error, SYNC_FAIL_MSG, { netMsg: NET_READ_MSG }), 'err');
      }
    } else if (r.data === 'noop') {
      toast(SYNC_NOOP_MSG);
    } else {
      toast('Refreshed', 'ok');
    }
  } finally {
    // finally, not the try tail: an unexpected throw must never strand the
    // button disabled with no way back except a page reload.
    _syncing = false;
    paintSync(false);
  }
}

// ─── Renderers (store → DOM) ───────────────────────────────────────────────

function renderSidebarNav() {
  const doc = /** @type {any} */ (globalThis).document;
  const nav = doc && doc.getElementById('nav');
  if (!nav) return;
  const ui = store.get(SLICES.UI) || {};
  const counts = store.get(SLICES.COUNTS) || {};
  nav.innerHTML = [TIERS.CARE, TIERS.OPERATE, TIERS.MANAGE]
    .map((tier) => {
      const routes = visibleRoutes(ui).filter((r) => r.tier === tier);
      if (!routes.length) return '';
      // The OPERATE tier is the one the mode switch hides — its label carries
      // the "Advanced" chip so nurses know why it comes and goes.
      const chip = tier === TIERS.OPERATE ? '<span class="tier-chip">Advanced</span>' : '';
      const items = routes
        .map((r) => {
          const badge = countBadge(counts, r.id);
          return (
            `<button class="nb${ui.view === r.id ? ' active' : ''}" data-action="nav" data-view="${esc(r.id)}">` +
            `<span class="nb-lbl">${esc(r.title)}</span>` +
            (badge !== null ? `<span class="cnt">${esc(badge)}</span>` : '') +
            '</button>'
          );
        })
        .join('');
      return `<div class="nav-group"><div class="nav-tier">${esc(TIER_LABELS[tier])}${chip}</div>${items}</div>`;
    })
    .join('');
}

/** Signed-in user card (legacy .nurse-card, fallbacks 'Nurse'/'—' kept). */
function userCardHTML() {
  const sess = store.get(SLICES.SESSION) || {};
  return (
    '<div class="nurse-card">' +
    `<div class="nc-av" aria-hidden="true">${esc(initials(sess.fullName))}</div>` +
    '<div class="nc-info">' +
    `<div class="nc-name">${esc(sess.fullName || 'Nurse')}</div>` +
    `<div class="nc-role">${esc(sess.role || '—')}</div></div>` +
    '<button class="nc-out" data-action="logout" title="Sign out" aria-label="Sign out">' +
    svg(
      '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>'
    ) +
    '</button></div>'
  );
}

function renderModeChip() {
  const doc = /** @type {any} */ (globalThis).document;
  const chip = doc && doc.getElementById('mode-chip');
  if (!chip) return;
  const ui = store.get(SLICES.UI) || {};
  // R-030: `modePersisted === false` means this browser refused to store the
  // preference. The chip must not imply a choice that will survive a refresh,
  // so the label carries the caveat and the title carries the reason. Absent
  // (undefined) reads as persisted — the boot path always sets it.
  const persisted = ui.modePersisted !== false;
  chip.textContent = modeChipLabel(ui.mode, persisted);
  chip.title = modeChipTitle(ui.mode, persisted);
  // It is a toggle: say so, and describe the action (title alone is not read).
  chip.setAttribute('aria-pressed', ui.mode === 'full' ? 'true' : 'false');
  chip.setAttribute('aria-label', modeChipLabel(ui.mode, persisted) + ' — ' + modeChipTitle(ui.mode, persisted));
  chip.classList.toggle('mode-chip-unsaved', !persisted);
}

function renderTitle() {
  const doc = /** @type {any} */ (globalThis).document;
  const el = doc && doc.getElementById('tb-title');
  if (!el) return;
  const ui = store.get(SLICES.UI) || {};
  const route = ROUTES.find((r) => r.id === ui.view);
  el.textContent = route ? route.title : '';
}

function renderMobTabs() {
  const doc = /** @type {any} */ (globalThis).document;
  const host = doc && doc.getElementById('mob-tabs-inner');
  if (!host) return;
  const ui = store.get(SLICES.UI) || {};
  host.innerHTML =
    MOB_TABS.map(
      (t) =>
        `<button class="mob-tab${ui.view === t.id ? ' active' : ''}" data-action="nav" data-view="${esc(t.id)}"` +
        `${ui.view === t.id ? ' aria-current="page"' : ''}>${svg(t.icon)}${esc(t.label)}</button>`
    ).join('');
}

/** Last view the shell painted — drives close-on-navigate for the drawer. */
let _lastView = null;

function renderAll() {
  // Tapping a drawer link navigates; the drawer must not stay over the view it
  // just took you to. Keyed on an actual view CHANGE so a mode toggle or a
  // counts refresh does not slam it shut under the user's thumb.
  const view = (store.get(SLICES.UI) || {}).view || null;
  if (_lastView !== null && view !== _lastView) closeDrawer();
  _lastView = view;
  renderSidebarNav();
  renderModeChip();
  renderTitle();
  renderMobTabs();
}

// ─── Mode toggle (legacy toggleUIMode, minus body-class CSS) ───────────────

function toggleMode() {
  const ui = store.get(SLICES.UI) || {};
  const sess = store.get(SLICES.SESSION) || {};
  const mode = nextMode(ui.mode);
  // R-030: the toggle NEVER depends on storage succeeding. The mode changes in
  // memory regardless; the write's outcome only decides how truthful the chip
  // and toast are about whether it will still be there tomorrow.
  const persisted = writeMode(sess.user && sess.user.id, mode);
  store.set(SLICES.UI, { mode, modePersisted: persisted });
  // If simple mode just hid the view we're standing on, land somewhere
  // visible (legacy toggleUIMode: import/billing/reports → today).
  if (ui.view && !canAccess(ui.view, { mode, isAdmin: ui.isAdmin })) navigate(DEFAULT_VIEW);
  toast(modeToast(mode, persisted), persisted ? 'ok' : '');
}

// ─── Mount ─────────────────────────────────────────────────────────────────

let _wired = false;

/**
 * Build the shell DOM and wire store subscriptions + actions. Idempotent
 * wiring; call after the UI slice is seeded (post-login).
 * @returns {void}
 */
export function mountShell() {
  const doc = /** @type {any} */ (globalThis).document;
  const app = doc && doc.getElementById('app');
  if (!app) return;
  app.innerHTML =
    '<div class="shell">' +
    // tabindex="-1": the drawer's focus trap parks focus on the panel itself
    // when it holds nothing focusable (trapStep's -2 sentinel).
    '<aside class="sidebar" id="sidebar" tabindex="-1"><div class="brand">Tumaini</div>' +
    '<nav id="nav" aria-label="Main"></nav>' +
    '<footer class="sb-foot">' +
    // Mode chip (R-030): the mode is visibly named, never a mystery state.
    '<button class="mode-chip" id="mode-chip" data-action="mode-toggle"></button>' +
    '<div id="sb-help-slot"></div>' +
    userCardHTML() +
    // APP_VERSION comes from `git describe` and already starts with the tag's
    // own "v" (e.g. "v2.0.0-phase0") — no extra prefix.
    `<div class="sb-ver">${esc(APP_VERSION)}</div>` +
    '</footer></aside>' +
    // The drawer scrim: a real element, so it hit-tests above the bottom bar.
    '<div class="nav-scrim" id="nav-scrim" data-action="nav-drawer-close" hidden></div>' +
    '<div class="main">' +
    '<header class="tb">' +
    // Phone-only (CSS), and the FIRST thing in the top bar so it is also the
    // first tab stop — the route out of the 5-tab bar to the other six views.
    '<button class="tb-burger" id="nav-burger" type="button" data-action="nav-drawer-toggle" ' +
    'aria-expanded="false" aria-controls="sidebar" aria-label="Main menu">' +
    '<span class="tb-burger-b" aria-hidden="true"></span>' +
    '</button>' +
    '<span class="tb-title" id="tb-title"></span>' +
    // Search placeholder: Cmd-K arrives in Phase 2 — focuses nothing yet.
    `<input class="tb-search" type="search" placeholder="Search patients…" readonly tabindex="-1" aria-disabled="true" aria-label="${esc(SEARCH_DISABLED_TITLE)}" title="${esc(SEARCH_DISABLED_TITLE)}"/>` +
    '<div class="tb-acts">' +
    // Sync (F-NAV-03). In the top bar, which is visible at every breakpoint —
    // so it is reachable on a phone WITHOUT going through the drawer, which is
    // the point: the offline chip now names a control the thumb can find.
    '<button class="tb-sync" id="tb-sync" type="button" data-action="sync" ' +
    'aria-label="Sync — reload this view">' +
    svg(SYNC_ICON) +
    '<span class="tb-sync-lbl">Sync</span></button>' +
    '<span id="prog-slot"></span>' + // programme pill (programmes.js)
    '<span id="live-slot"></span>' + // Live strip chip (realtime.js)
    '<span id="help-slot"></span>' + // Help button (views/help.js)
    '</div></header>' +
    '<main id="view" class="view" tabindex="-1"></main>' +
    '<nav class="mob-tabs" aria-label="Primary"><div class="mob-tabs-inner" id="mob-tabs-inner"></div></nav>' +
    '</div></div>';

  // Seed the counts slice once — Phase 2 loaders overwrite with real numbers.
  if (!store.get(SLICES.COUNTS)) store.set(SLICES.COUNTS, Object.assign({}, COUNT_SEED));

  // Defensive: this shell starts with the drawer shut, whatever module state
  // or leftover DOM preceded it. setDrawer(false) applies the whole closed
  // plan — `.main` loses inert + aria-hidden, the body loses nav-locked, the
  // scrim hides, the burger reads aria-expanded="false", the Tab trap comes
  // off. Then watch the breakpoint, because above it nothing on screen can
  // close a drawer that is already open (drawerShouldClose).
  setDrawer(false);
  watchViewport();

  if (!_wired) {
    _wired = true;
    register('mode-toggle', toggleMode);
    register('sync', () => void doSync());
    register('nav-drawer-toggle', () => setDrawer(drawerNext(_drawerOpen, 'toggle')));
    register('nav-drawer-close', () => closeDrawer());
    // The skip link's handler. Registered here rather than in main.js because
    // the demo entry point wires its own action set and would otherwise ship
    // the control without its behaviour.
    register('skip-main', () => void focusView());
    // Escape closes it, like every other overlay in the app.
    doc.addEventListener('keydown', (/** @type {any} */ e) => {
      if (e.key === 'Escape') closeDrawer();
    });
    store.subscribe(SLICES.UI, renderAll);
    store.subscribe(SLICES.COUNTS, renderSidebarNav);
    store.subscribe(SLICES.SESSION, () => {
      // Role/name changes are rare (re-login) — cheapest correct response is
      // a full remount next time; for now just re-render nav gating.
      renderAll();
    });
  }
  renderAll();
}
